import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';

const AIChat = forwardRef(({ isOpen: externalIsOpen, setIsOpen: externalSetIsOpen }, ref) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Sync external and internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalSetIsOpen !== undefined ? externalSetIsOpen : setInternalIsOpen;

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    sendSecurityContext: (contextMessage) => {
      setMessages(prev => [...prev, {
        type: 'bot',
        text: contextMessage,
        timestamp: new Date()
      }]);
      setIsOpen(true);
    }
  }));

  // Debug log
  useEffect(() => {
    console.log('AIChat component mounted, isOpen:', isOpen);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        type: 'bot',
        text: 'Hallo! 👋 Ich bin Ihr KI-Assistent von AB-Informatik. Ich kann Ihnen bei Fragen zu unseren IT-Services, Security-Assessments und Lösungen helfen. Wie kann ich Ihnen heute weiterhelfen?',
        timestamp: new Date()
      }]);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: inputValue,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      const botMessage = {
        type: 'bot',
        text: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        type: 'bot',
        text: 'Entschuldigung, es gab ein Problem. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter +49 2241 261310.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Toggle Button - Right Side */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: '120px',
          right: isOpen ? '380px' : '20px',
          width: '50px',
          height: '50px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #0071e3, #00a8ff)',
          color: 'white',
          border: '3px solid white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 4px 16px rgba(0, 113, 227, 0.6)',
          zIndex: 999998,
          transition: 'right 0.3s ease'
        }}
        className="ai-chat-toggle-button"
        aria-label={isOpen ? "Chat schließen" : "Chat öffnen"}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        {!isOpen && (
          <span style={{
            position: 'absolute',
            top: '-5px',
            right: '-5px',
            background: '#ff3b30',
            color: 'white',
            fontSize: '10px',
            fontWeight: '700',
            padding: '3px 6px',
            borderRadius: '10px',
            border: '2px solid white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>KI</span>
        )}
      </button>

      {/* Chat Sidebar */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: '0',
          right: '0',
          width: window.innerWidth <= 768 ? '100%' : '380px',
          height: '100vh',
          background: 'var(--bg-secondary)',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 999999,
          animation: 'slideInRight 0.3s ease'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #0071e3, #00a8ff)',
            color: 'white',
            padding: window.innerWidth <= 768 ? '16px' : '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MessageCircle size={window.innerWidth <= 768 ? 18 : 20} />
              <div>
                <h3 style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '14px' : '16px', fontWeight: 600 }}>KI-Assistent</h3>
                <p style={{ margin: 0, fontSize: window.innerWidth <= 768 ? '11px' : '12px', opacity: 0.9 }}>Online</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: window.innerWidth <= 768 ? '16px' : '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            paddingBottom: window.innerWidth <= 768 ? '80px' : '20px'
          }} className="ai-chat-messages">
            {messages.map((message, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxWidth: '85%',
                  alignSelf: message.type === 'user' ? 'flex-end' : 'flex-start',
                  animation: 'slideIn 0.3s ease'
                }}
              >
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  wordWrap: 'break-word',
                  background: message.type === 'user' ? '#0071e3' : 'var(--bg-tertiary)',
                  color: message.type === 'user' ? 'white' : 'var(--text-primary)',
                  borderBottomRightRadius: message.type === 'user' ? '4px' : '12px',
                  borderBottomLeftRadius: message.type === 'user' ? '12px' : '4px'
                }}>
                  {message.text}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'var(--text-tertiary)',
                  marginTop: '4px',
                  padding: '0 4px'
                }}>
                  {message.timestamp.toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            ))}
            {isLoading && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                maxWidth: '85%',
                alignSelf: 'flex-start'
              }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  background: 'var(--bg-tertiary)',
                  borderBottomLeftRadius: '4px'
                }}>
                  <div className="ai-chat-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: window.innerWidth <= 768 ? '12px' : '16px',
            background: 'var(--bg-tertiary)',
            display: 'flex',
            gap: '8px',
            alignItems: 'flex-end',
            borderTop: '1px solid var(--border-subtle)',
            position: window.innerWidth <= 768 ? 'fixed' : 'relative',
            bottom: window.innerWidth <= 768 ? '0' : 'auto',
            left: window.innerWidth <= 768 ? '0' : 'auto',
            right: window.innerWidth <= 768 ? '0' : 'auto',
            width: window.innerWidth <= 768 ? '100%' : 'auto',
            boxSizing: 'border-box',
            flexShrink: 0
          }}>
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nachricht..."
              style={{
                flex: 1,
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '12px',
                padding: window.innerWidth <= 768 ? '10px 12px' : '12px 16px',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'none',
                maxHeight: window.innerWidth <= 768 ? '60px' : '120px',
                minHeight: '40px'
              }}
              rows="1"
              disabled={isLoading}
            />
            <button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              style={{
                background: '#0071e3',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                opacity: inputValue.trim() && !isLoading ? 1 : 0.5,
                flexShrink: 0
              }}
              aria-label="Nachricht senden"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
});

AIChat.displayName = 'AIChat';

export default AIChat;
